/**
 * HistorySection.tsx
 * ----------------
 * Section for viewing the history of live site events.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { useBoolean } from '@fluentui/react-hooks';
import * as React from 'react';

import {
    Americas,
    AsiaPacific,
    Europe,
    MiddleEastAndAfrica
} from '../../constants/geographies';
import { LiveSiteEventSeverity } from '../../models/status';
import { LiveSiteEventItem } from '../../models/ui/liveSiteEvent';
import {
    Last24Hours,
    Last30Days,
    Last48Hours,
    Last60Days,
    Last7Days,
    Last90Days,
    TimeRangeValue
} from '../../models/ui/status';
import { getTitleForGeography } from '../../utilities/geographies';
import {
    getDisplayNameForService,
    getIconNameForService
} from '../../utilities/services';
import { ActiveLiveSiteEventsCard } from './ActiveLiveSiteEventsCard';
import { CollectionEmptyState } from './CollectionEmptyState';
import { FilterBar, MultiSelectFilter, SingleSelectFilter } from './FilterBar';
import { ResolvedLiveSiteEventsCard } from './ResolvedLiveSiteEventsCard';

/**
 * Component properties
 */

interface HistorySectionProps {
    activeLiveSiteEvents: LiveSiteEventItem[];
    geographies: string[];
    liveSiteEventUrl: (id: number) => string;
    onGeographiesChanged: (geographies: string[]) => void;
    onSearchSubmitted: (search: string) => void;
    onServicesChanged: (services: string[]) => void;
    onSeveritiesChanged: (severities: LiveSiteEventSeverity[]) => void;
    onTimeChanged: (time: TimeRangeValue) => void;
    resolvedLiveSiteEvents: LiveSiteEventItem[];
    services: string[];
    severities: LiveSiteEventSeverity[];
    time: TimeRangeValue;
}

/**
 * Section for viewing the history of live site events.
 * @param props Component properties
 * @returns Rendered content
 */
export function HistorySection(props: HistorySectionProps) {
    const {
        activeLiveSiteEvents,
        geographies,
        liveSiteEventUrl,
        onGeographiesChanged,
        onSearchSubmitted,
        onServicesChanged,
        onSeveritiesChanged,
        onTimeChanged,
        resolvedLiveSiteEvents,
        services,
        severities,
        time,
    } = props;

    // Component state hooks
    const [
        showFilterBar,
        { setFalse: setShowFilterBarFalse, toggle: toggleShowFilterBar },
    ] = useBoolean(false);

    // Callback hooks
    const onFilterBarButtonClicked = React.useCallback(
        () => toggleShowFilterBar(),
        [toggleShowFilterBar]
    );
    const onFilterBarClosed = React.useCallback(
        () => setShowFilterBarFalse(),
        [setShowFilterBarFalse]
    );

    const onGeographiesFilterChanged = React.useCallback(
        (values: string[]) => {
            onGeographiesChanged(values);
        },
        [onGeographiesChanged]
    );

    const onServicesFilterChanged = React.useCallback(
        (values: string[]) => {
            onServicesChanged(values);
        },
        [onServicesChanged]
    );

    const onSeveritiesFilterChanged = React.useCallback(
        (values: string[]) => {
            onSeveritiesChanged(values as LiveSiteEventSeverity[]);
        },
        [onSeveritiesChanged]
    );

    const onTimeFilterChanged = React.useCallback(
        (value: string) => {
            onTimeChanged(value as TimeRangeValue);
        },
        [onTimeChanged]
    );

    // TODO: add severity icons to filter dropdown
    return (
        <div className="history-section">
            {activeLiveSiteEvents.length > 0 && (
                <div className="history-section__card-header">
                    <h2 id="history-section-active"
                        className="history-section__card-title"
                    >
                        Active
                    </h2>
                </div>
            )}

            {activeLiveSiteEvents.length > 0 && (
                <div className="history-section__card-container">
                    <ActiveLiveSiteEventsCard
                        liveSiteEvents={activeLiveSiteEvents}
                        liveSiteEventUrl={liveSiteEventUrl}
                    />
                </div>
            )}

            <div className="history-section__card-header">
                <h2 id="history-section-resolved"
                    className="history-section__card-title"
                >
                    Resolved
                </h2>

                <IconButton
                    ariaLabel="Filter"
                    className="history-section__filter-button"
                    iconProps={{ iconName: 'Filter' }}
                    onClick={onFilterBarButtonClicked}
                    title="Filter"
                />
            </div>

            {showFilterBar && (
                <div className="history-section__filter-bar-container">
                    <FilterBar
                        hasSearch
                        isClosable
                        onClose={onFilterBarClosed}
                        onSearchChanged={onSearchSubmitted}
                    >
                        <MultiSelectFilter
                            label="Severity"
                            onChange={onSeveritiesFilterChanged}
                            options={[
                                {
                                    icon: 'Advisory',
                                    key: 'Advisory',
                                    text: 'Advisory',
                                },
                                {
                                    icon: 'Degraded',
                                    key: 'Degraded',
                                    text: 'Degraded',
                                },
                                {
                                    icon: 'Unhealthy',
                                    key: 'Unhealthy',
                                    text: 'Unhealthy',
                                },
                            ]}
                            placeholder="Severity"
                            selectedKeys={severities}
                        />

                        <MultiSelectFilter
                            label="Services"
                            onChange={onServicesFilterChanged}
                            options={[
                                {
                                    icon: getIconNameForService('VSFeedback'),
                                    key: 'VSFeedback',
                                    text: getDisplayNameForService(
                                        'VSFeedback'
                                    ),
                                },
                                {
                                    icon: getIconNameForService(
                                        'IdentitySignIn'
                                    ),
                                    key: 'IdentitySignIn',
                                    text: getDisplayNameForService(
                                        'IdentitySignIn'
                                    ),
                                },
                                {
                                    icon: getIconNameForService('Intellicode'),
                                    key: 'Intellicode',
                                    text: getDisplayNameForService(
                                        'Intellicode'
                                    ),
                                },
                                {
                                    icon: getIconNameForService('Licensing'),
                                    key: 'Licensing',
                                    text: getDisplayNameForService('Licensing'),
                                },
                                {
                                    icon: getIconNameForService('LiveShare'),
                                    key: 'LiveShare',
                                    text: getDisplayNameForService('LiveShare'),
                                },
                                {
                                    icon: getIconNameForService('Marketplace'),
                                    key: 'Marketplace',
                                    text: getDisplayNameForService(
                                        'Marketplace'
                                    ),
                                },
                                {
                                    icon: getIconNameForService('NuGet'),
                                    key: 'NuGet',
                                    text: getDisplayNameForService('NuGet'),
                                },
                                {
                                    icon: getIconNameForService('Roaming'),
                                    key: 'Roaming',
                                    text: getDisplayNameForService('Roaming'),
                                },
                                {
                                    icon: getIconNameForService(
                                        'VisualStudioSetup'
                                    ),
                                    key: 'VisualStudioSetup',
                                    text: getDisplayNameForService(
                                        'VisualStudioSetup'
                                    ),
                                },
                            ]}
                            placeholder="Service"
                            selectedKeys={services}
                        />

                        <SingleSelectFilter
                            label="Date"
                            onChange={onTimeFilterChanged}
                            options={[
                                { key: Last24Hours, text: 'Last 24 Hours' },
                                { key: Last48Hours, text: 'Last 48 Hours' },
                                { key: Last7Days, text: 'Last 7 Days' },
                                { key: Last30Days, text: 'Last 30 Days' },
                                { key: Last60Days, text: 'Last 60 Days' },
                                { key: Last90Days, text: 'Last 90 Days' },
                            ]}
                            placeholder="Date"
                            selectedKey={time}
                        />

                        <MultiSelectFilter
                            label="Geographies"
                            onChange={onGeographiesFilterChanged}
                            options={[
                                {
                                    key: Americas,
                                    text: getTitleForGeography(
                                        Americas
                                    ) as string,
                                },
                                {
                                    key: AsiaPacific,
                                    text: getTitleForGeography(
                                        AsiaPacific
                                    ) as string,
                                },
                                {
                                    key: Europe,
                                    text: getTitleForGeography(
                                        Europe
                                    ) as string,
                                },
                                {
                                    key: MiddleEastAndAfrica,
                                    text: getTitleForGeography(
                                        MiddleEastAndAfrica
                                    ) as string,
                                },
                            ]}
                            placeholder="Geography"
                            selectedKeys={geographies}
                        />
                    </FilterBar>
                </div>
            )}

            <div className="history-section__card-container history-section__card-container--last">
                {resolvedLiveSiteEvents.length > 0 && (
                    <ResolvedLiveSiteEventsCard
                        liveSiteEvents={resolvedLiveSiteEvents}
                        liveSiteEventUrl={liveSiteEventUrl}
                    />
                )}

                {resolvedLiveSiteEvents.length <= 0 && (
                    <div className="history-section__empty-state-card ms-depth-8">
                        <CollectionEmptyState message='No live site events match the current filters. Click the "Filter" button above to modify your filters.' />
                    </div>
                )}
            </div>
        </div>
    );
}
